import { TFunction } from 'i18next';
import { DisciplineEnum, TNamespaceEnum } from 'types/types';
import {
    FootballStateEnum,
    FutsalStateEnum,
    MemberDetailApiInterface,
    PersonDetailsApiInterface,
    TagApiInterface,
} from 'submodules/api_middleware';
import { getTranslation } from 'utils/localization';

const TAG_TYPE_COLORS = {
    MEMBERSHIP: {
        active: '#3BE07B',
        inactive: '#DE1818',
        new: '#DE1818',
        archive: '#36383e',
        active_next_year_deadline: '#F1B137',
        active_next_year_deadline_protection: '#F1B137',
    },
    LEAGUE: '#F7D855',
    CONTRACT_TYPE: '#37A8FC',
    CLUB_MANAGER: '#A76b1C',
    ROLE: {
        player: '#37A8FC',
        referee: '#37fcfc',
        delegate: '#55bebe',
        coach: '#6780ff',
        agent: '#7e90f5',
        technical_delegate: '#55bebe',
        masseur: '#55a0be',
        paramedic: '#39819c',
        physician: '#1aa6e0',
    },
};

interface Props {
    t: TFunction;
    person: PersonDetailsApiInterface;
    member?: MemberDetailApiInterface;
    memberStatus: FootballStateEnum | FutsalStateEnum;
}

export const getPersonTags = ({
    t,
    person,
    member,
    memberStatus,
}: Props): TagApiInterface[] => {
    const tags: TagApiInterface[] = [];

    // A member role can be actually held even by non-members
    // who don't have any memberStatus.
    if (member && memberStatus) {
        tags.push({
            key: 'membership',
            value: t(`Members.${memberStatus}`),
            bcgColor:
                TAG_TYPE_COLORS.MEMBERSHIP[
                    `${memberStatus}` as FootballStateEnum | FutsalStateEnum
                ],
            color:
                TAG_TYPE_COLORS.MEMBERSHIP[
                    `${memberStatus}` as FootballStateEnum | FutsalStateEnum
                ] === TAG_TYPE_COLORS.MEMBERSHIP.archive
                    ? '#FFF'
                    : undefined,
        });
    }

    person.roles.forEach((pRole, index) => {
        let role = Object.keys(TAG_TYPE_COLORS.ROLE).find(
            (r) => r === pRole.role
        );
        const bgColor = pRole.is_archived
            ? TAG_TYPE_COLORS.MEMBERSHIP.archive
            : // @ts-ignore-next-line
              TAG_TYPE_COLORS.ROLE[`${role}`];
        const fontColor = pRole.is_archived ? '#FFF' : undefined;

        if (role)
            tags.push({
                key: `${pRole.role}-${index}`,
                value: getTranslation(t, TNamespaceEnum.person, `role.${role}`),
                color: fontColor,
                bcgColor: bgColor,
            });

        role = Object.keys(TAG_TYPE_COLORS.ROLE).find(
            (r) => `${r}-${DisciplineEnum.football}` === pRole.role
        );
        if (role)
            tags.push({
                key: `${pRole.role}-${index}`,
                value:
                    getTranslation(t, TNamespaceEnum.person, `role.${role}`) +
                    ` ${getTranslation(
                        t,
                        TNamespaceEnum.person,
                        'ofFootball'
                    )}`,
                color: fontColor,
                bcgColor: bgColor,
            });

        role = Object.keys(TAG_TYPE_COLORS.ROLE).find(
            (r) => `${r}-${DisciplineEnum.futsal}` === pRole.role
        );
        if (role)
            tags.push({
                key: `${pRole.role}-${index}`,
                value:
                    getTranslation(t, TNamespaceEnum.person, `role.${role}`) +
                    ` ${getTranslation(t, TNamespaceEnum.person, 'ofFutsal')}`,
                color: fontColor,
                bcgColor: bgColor,
            });
    });

    // --- Targito tag ---
    if (person.targito_card_issued_successfully) {
        const targitoTag: TagApiInterface = {
            key: 'targito',
            value: getTranslation(t, TNamespaceEnum.person, 'targito.card_tag'),
            bcgColor: '#eaf1f8',
        };

        tags.push(targitoTag);
    }

    return tags;
};
